import React, { memo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess, hasOrgAdminAccess, hasAdminAccess } from '@/context/auth/types';
import { useRestaurantBranding } from '@/hooks/useRestaurantBranding';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface NavigationHeaderProps {
  collapsed: boolean;
  toggleSidebar: () => void;
}

const NavigationHeader: React.FC<NavigationHeaderProps> = memo(({ collapsed, toggleSidebar }) => {
  const { getLogoUrl, getActiveName } = useRestaurantBranding();
  const { user } = useAuth();
  
  const logoUrl = getLogoUrl();
  const displayName = getActiveName();
  
  // Get the effective role (JWT role takes precedence if available)
  const effectiveRole = user?.jwt_role || user?.role;
  

  return (
    <div className="flex flex-col gap-2 px-3 py-4 relative">
      {!collapsed ? (
        <div className="flex items-center gap-2 justify-start">
          {logoUrl ? (
            <Avatar className="h-8 w-8 rounded-md">
              <AvatarImage src={logoUrl} alt={displayName} />
              <AvatarFallback className="bg-primary/10 rounded-md">
                <Store className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
          ) : (
            <Avatar className="h-8 w-8 rounded-md bg-primary/10">
              <AvatarFallback>
                <Store className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
          )}
          
          <span className="text-sm font-medium truncate max-w-[140px]">
            {displayName}
          </span>
        </div>
      ) : (
        <div className="h-8"></div>
      )}
      
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-7 w-7 rounded-full z-20",
          collapsed 
            ? "absolute top-4 left-1/2 transform -translate-x-1/2" 
            : "absolute right-3 top-4"
        )}
        onClick={toggleSidebar}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
        <span className="sr-only">Toggle navigation</span>
      </Button>
    </div>
  );
});

NavigationHeader.displayName = 'NavigationHeader';

export default NavigationHeader;
